import { useRef } from 'react';

import { toApiBoolean } from '@liquidweb-dev/club-sauce';
import type { LWApiPublicBillingStripePaymentBeginPaymentResultRaw } from '@liquidweb-dev/club-sauce/public/billing/stripe/payment/index.raw';
import toast from '@liquidweb-dev/undercurrent/toast';
import { useStripe } from '@liquidweb-dev/undercurrent/stripe';
import { useQueryClient } from '@tanstack/react-query';

import useBillingStripeBeginPayment from 'modules/queries/billing/stripe/payment/useBeginPayment';
import useStripeConfirmPayment from 'modules/queries/billing/stripe/payment/useConfirmPayment';
import useStripeConfirmSavedCardPayment from 'modules/queries/billing/stripe/payment/useConfirmSavedCardPayment';
import useFetchBillingStripePaymentDetails from 'modules/queries/billing/stripe/payment/useFetchDetails';
import useIsBillingStripePaymentInFlight from 'modules/queries/billing/stripe/payment/useIsPaymentInFlight';
import useBillingStripePaymentMake from 'modules/queries/billing/stripe/payment/useMake';
import useBillingStripePaymentMarkDeclined from 'modules/queries/billing/stripe/payment/useMarkDeclined';
import { BILLING_BALANCE_QUERY_KEY } from 'modules/queries/billing/useBalance';
import {
	useDialogMakePaymentContext,
	type HeldStripePaymentI,
} from './dialogMakePaymentContext';
import reportPaymentFailure from './reportPaymentFailure';

export type StripePayNowParams =
	| {
			/** a card entered in the dialog, carried here from Continue */
			kind: 'newCard';
			amount: string;
			confirmationToken: string;
			savePaymentMethod: boolean;
	  }
	| {
			/** a Stripe card the account already has on file */
			kind: 'savedCard';
			amount: string;
			paymentInstrumentId: number;
	  };

/** Shown once the card is held, so it never suggests paying another way. */
const reportCaptureFailure = () =>
	toast.danger('We couldn’t complete your payment', {
		description:
			'Please try again, and contact support if this keeps happening.',
	});

/**
 * Takes a Stripe payment in one action - begin, confirm and capture - and
 * retries a failed capture on the payment already confirmed.
 */
const useStripePayNow = () => {
	const queryClient = useQueryClient();
	const stripe = useStripe();
	const { heldStripePayment, onSubmitSuccess, setHeldStripePayment, setStep } =
		useDialogMakePaymentContext();

	const { mutateAsync: beginPayment } = useBillingStripeBeginPayment();
	const { mutateAsync: confirmPayment } = useStripeConfirmPayment(stripe);
	const { mutateAsync: confirmCardPayment } =
		useStripeConfirmSavedCardPayment(stripe);
	const { mutateAsync: makePayment } = useBillingStripePaymentMake();
	const { mutate: markPaymentDeclined } = useBillingStripePaymentMarkDeclined();
	const fetchPaymentDetails = useFetchBillingStripePaymentDetails();

	const isPaying = useIsBillingStripePaymentInFlight();
	// `isPaying` reaches the button a render late, so a second click in between
	// would begin and capture a second payment
	const isPayNowRunningRef = useRef(false);

	/** Captures a confirmed intent, returning whether the payment went through. */
	const capture = async ({ id, amount }: HeldStripePaymentI) => {
		try {
			await makePayment({ amount, payment_intent_id: id });

			return true;
		} catch {
			reportCaptureFailure();

			return false;
		}
	};

	/** Retries a failed capture, without capturing again one that already landed. */
	const retryCapture = async (confirmedIntent: HeldStripePaymentI) => {
		try {
			const { status } = await fetchPaymentDetails({
				payment_intent_id: confirmedIntent.id,
			});

			if (status === 'succeeded') {
				queryClient.invalidateQueries({
					queryKey: [BILLING_BALANCE_QUERY_KEY],
				});

				return true;
			}
		} catch {
			reportCaptureFailure();

			return false;
		}

		return capture(confirmedIntent);
	};

	/** Begins, confirms and captures a new payment, returning whether it went through. */
	const pay = async (params: StripePayNowParams) => {
		let begun: LWApiPublicBillingStripePaymentBeginPaymentResultRaw;

		try {
			begun = await beginPayment(
				params.kind === 'newCard'
					? {
							amount: params.amount,
							save_payment_method: toApiBoolean(params.savePaymentMethod),
						}
					: {
							amount: params.amount,
							payment_instrument_id: params.paymentInstrumentId,
						},
			);
		} catch (error) {
			reportPaymentFailure(error);

			return false;
		}

		try {
			if (params.kind === 'newCard') {
				await confirmPayment({
					clientSecret: begun.client_secret,
					confirmationToken: params.confirmationToken,
				});
			} else {
				await confirmCardPayment({ clientSecret: begun.client_secret });
			}
		} catch (error) {
			markPaymentDeclined({ payment_intent_id: begun.payment_intent_id });
			reportPaymentFailure(error);
			setStep('start');

			return false;
		}

		// the card is held from here on, so a retry must land on this intent, and
		// the dialog stays open until it does
		const held = { id: begun.payment_intent_id, amount: params.amount };
		setHeldStripePayment(held);

		return capture(held);
	};

	const payNow = async (params: StripePayNowParams) => {
		if (isPayNowRunningRef.current) return;

		isPayNowRunningRef.current = true;
		let isPaid: boolean;
		try {
			isPaid = heldStripePayment
				? await retryCapture(heldStripePayment)
				: await pay(params);
		} finally {
			isPayNowRunningRef.current = false;
		}

		if (!isPaid) return;

		setHeldStripePayment(null);
		onSubmitSuccess();
	};

	return { payNow, isPaying };
};

export default useStripePayNow;
